import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import PrintProductConfiguratorHeaderPane from './components/PrintProductConfiguratorHeaderPane';
import PrintProductConfiguratorControlsPane from './components/PrintProductConfiguratorControlsPane';
import PrintProductConfiguratorPreviewPane from './components/PrintProductConfiguratorPreviewPane';
import usePrintProductConfiguration from './usePrintProductConfiguration';
import PrintProductMountingOptionsAlertModal, {
  PRINT_PRODUCT_MOUNTING_OPTIONS_ALERT_MODAL,
} from './components/PrintProductMountingOptionsAlertModal/PrintProductMountingOptionsAlertModal';
import { shouldAlertPrintMountingOptions } from './components/PrintProductMountingOptionsAlertModal/helpers';
import useOpenModal from '../../../../hooks/useOpenModal';
import DesignEditorLoadingIndicator from '../../../../components/global/DesignEditor/components/DesignEditorLoadingIndicator';
import DesignEditor from '../../../../components/global/DesignEditor';
import { ORIENTATION } from '../../../cards/constants';
import AccountCardEmptyTextAlertModal, {
  ACCOUNT_CARD_EMPTY_TEXT_ALERT_MODAL,
} from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/components/AccountCardEmptyTextAlertModal/AccountCardEmptyTextAlertModal';
import { shouldAlertEmptyText } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/components/AccountCardEmptyTextAlertModal/helpers';

const propTypes = {
  className: PropTypes.string,
  printProductId: PropTypes.string.isRequired,
  printProductSizeId: PropTypes.string,
  availablePrintProductSizeIds: PropTypes.arrayOf(PropTypes.string),
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  defaultAccountCard: PropTypes.shape({
    id: PropTypes.string,
  }),
  buttonText: PropTypes.node,
  controlsDisabled: PropTypes.bool,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  presetCardElementConfigurationInputs: PropTypes.object,
};

const PrintProductConfigurator = ({
  className,
  printProductId,
  printProductSizeId,
  printProductSizeServiceIds,
  availablePrintProductSizeIds = [],
  photo,
  orientation,
  onClose,
  onComplete,
  defaultAccountCard,
  buttonText,
  controlsDisabled = false,
  albumMembershipGroup,
  identity,
  presetCardElementConfigurationInputs,
}) => {
  const classes = classNames({
    [className]: !!className,
    'print-product-configurator': true,
  });

  const {
    globalPrintProductAlbumCustomPrint,
    printProduct,
    printProductConfiguration: {
      selectedPrintProductSize,
      handlePrintProductSizeSelect,
      selectedPrintProductMount,
      handlePrintProductMountSelect,
      selectedCardSide,
      handleCardSideSelect,
    },
    accountCardConfiguration: {
      loadingAccountCard,
      accountCard,
      onElementConfigurationChange,
      cardElementLoading,
      cardSides,
    },
  } = usePrintProductConfiguration({
    printProductId,
    defaultPrintProductSizeId: printProductSizeId,
    defaultPrintProductSizeServiceIds: printProductSizeServiceIds,
    defaultPhoto: photo,
    defaultAccountCardId: defaultAccountCard?.id,
    defaultOrientation: orientation,
    presetCardElementConfigurationInputs,
  });
  const openModal = useOpenModal();

  if (!printProduct) {
    return <DesignEditorLoadingIndicator />;
  }

  const handleComplete = () => {
    onComplete({
      accountCard,
      albumCustomPrint: globalPrintProductAlbumCustomPrint,
      printProduct,
    });
  };

  return (
    <DesignEditor className={classes}>
      <PrintProductConfiguratorHeaderPane
        buttonText={buttonText}
        printProduct={printProduct}
        onClose={onClose}
        onComplete={() => {
          if (shouldAlertEmptyText({ accountCard })) {
            openModal(ACCOUNT_CARD_EMPTY_TEXT_ALERT_MODAL, {
              accountCard,
              onComplete: handleComplete,
            });
          } else if (
            shouldAlertPrintMountingOptions({
              selectedPrintProductSize,
              selectedPrintProductMount,
            })
          ) {
            openModal(PRINT_PRODUCT_MOUNTING_OPTIONS_ALERT_MODAL);
          } else {
            handleComplete();
          }
        }}
        completeDisabled={!accountCard}
      />

      {!controlsDisabled && (
        <PrintProductConfiguratorControlsPane
          printProduct={printProduct}
          selectedPrintProductSize={selectedPrintProductSize}
          availablePrintProductSizeIds={availablePrintProductSizeIds}
          handlePrintProductSizeSelect={handlePrintProductSizeSelect}
          selectedPrintProductMount={selectedPrintProductMount}
          handlePrintProductMountSelect={handlePrintProductMountSelect}
          cardSides={cardSides}
          selectedCardSide={selectedCardSide}
          handleCardSideSelect={handleCardSideSelect}
        />
      )}

      <PrintProductConfiguratorPreviewPane
        accountCard={accountCard}
        loading={loadingAccountCard}
        onElementConfigurationChange={onElementConfigurationChange}
        cardElementLoading={cardElementLoading}
        printProduct={printProduct}
        albumMembershipGroup={albumMembershipGroup}
        identity={identity}
        defaultPhoto={photo}
      />

      {selectedPrintProductSize && (
        <PrintProductMountingOptionsAlertModal
          printProduct={printProduct}
          selectedPrintProductSize={selectedPrintProductSize}
          selectedPrintProductMount={selectedPrintProductMount}
          handlePrintProductMountSelect={handlePrintProductMountSelect}
          onComplete={handleComplete}
        />
      )}
      <AccountCardEmptyTextAlertModal />
    </DesignEditor>
  );
};

PrintProductConfigurator.propTypes = propTypes;

export default PrintProductConfigurator;
